import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from 'Context/CartContext';
import { assetUrl } from 'Utils/api';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import * as S from './CartPage.style';

const CartPage: React.FC = () => {
  const { t } = useTranslation();
  const { items, total, removeFromCart, refreshCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const handleRemove = async (watchId: string) => {
    await removeFromCart(watchId);
  };

  const handleCheckout = () => {
    if (items.length === 0) return;
    navigate('/checkout/adresse');
  };

  const handleContinueShopping = () => {
    navigate('/kaufen');
  };

  // Get the earliest expiration time (should be same for all items)
  const getCartExpirationTime = () => {
    if (items.length === 0) return null;
    const firstItem = items[0];
    return new Date(firstItem.reservedUntil);
  };

  const [timeLeft, setTimeLeft] = React.useState<number>(0);

  React.useEffect(() => {
    const updateTimer = () => {
      const expirationTime = getCartExpirationTime();
      if (!expirationTime) {
        setTimeLeft(0);
        return;
      }

      const now = Date.now();
      const diff = expirationTime.getTime() - now;
      setTimeLeft(Math.max(0, Math.floor(diff / 1000))); // seconds
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [items]);

  const minutesLeft = Math.floor(timeLeft / 60);
  const secondsLeft = timeLeft % 60;

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.PageTitle>{t('cart.page_title')}</S.PageTitle>

        {items.length === 0 ? (
          <S.EmptyCart>
            <S.EmptyIcon>
              <img src="/assets/images/icons/empty-cart-bag.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </S.EmptyIcon>
            <h2>{t('cart.empty_title')}</h2>
            <p>{t('cart.empty_text')}</p>
            <S.ContinueButton onClick={handleContinueShopping}>
              {t('cart.to_overview')}
            </S.ContinueButton>
          </S.EmptyCart>
        ) : (
          <S.ContentGrid>
            <S.ItemsSection>
              {/* Global Timer for entire cart */}
              <S.GlobalTimer $warning={minutesLeft < 5}>
                ⏱ {t('cart.timer', { min: minutesLeft, sec: secondsLeft.toString().padStart(2, '0') })}
              </S.GlobalTimer>

              {items.map((item) => {
                return (
                  <S.CartItem key={item.id}>
                    <S.ItemImage>
                      {item.image ? (
                        <img src={assetUrl(item.image)} alt={`${item.brand} ${item.model}`} />
                      ) : (
                        <S.NoImagePlaceholder>{t('cart.no_image')}</S.NoImagePlaceholder>
                      )}
                    </S.ItemImage>

                    <S.ItemDetails>
                      <S.ItemBrand>{item.brand}</S.ItemBrand>
                      <S.ItemModel>{item.model}</S.ItemModel>
                      {item.referenceNumber && (
                        <S.ItemRef>{t('cart.ref_prefix')} {item.referenceNumber}</S.ItemRef>
                      )}
                    </S.ItemDetails>

                    <S.ItemPrice>{item.priceEur.toLocaleString('de-DE')} €</S.ItemPrice>

                    <S.RemoveButton data-testid={`cart-item-remove-${item.id}`} onClick={() => handleRemove(item.id)}>
                      ✕
                    </S.RemoveButton>
                  </S.CartItem>
                );
              })}
            </S.ItemsSection>

            <S.SummarySection>
              <S.SummaryTitle>{t('cart.summary_title')}</S.SummaryTitle>

              <S.SummaryRow>
                <span>{t('cart.subtotal')}</span>
                <span>{total.toLocaleString('de-DE')} €</span>
              </S.SummaryRow>

              <S.SummaryRow>
                <span>{t('cart.shipping')}</span>
                <span>{t('cart.shipping_calculated')}</span>
              </S.SummaryRow>

              <S.Divider />

              <S.SummaryTotal>
                <span>{t('cart.total')}</span>
                <span>{total.toLocaleString('de-DE')} €</span>
              </S.SummaryTotal>

              <S.CheckoutButton onClick={handleCheckout} data-testid="cart-checkout-btn">
                {t('cart.to_checkout')}
              </S.CheckoutButton>

              <S.ContinueShoppingLink onClick={handleContinueShopping}>
                {t('cart.continue_shopping')}
              </S.ContinueShoppingLink>

              <S.SecurityNote>
                <span><img src="/assets/images/icons/secure-payment.png" alt="" style={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }} /></span>
                <p>{t('cart.secure_payment')}</p>
              </S.SecurityNote>
            </S.SummarySection>
          </S.ContentGrid>
        )}
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default CartPage;
